import React, { Fragment, useEffect, useRef, useState } from 'react';

import { useQuickSearchMutation } from 'Services/quickSearchApi';
import { BreakPointHooks } from 'Hooks/BreakPointHooks';
import useDebounce from 'Hooks/useDebounce';
import useLockedBody from 'Hooks/useLockedBody';
import useOutsideClick from 'Hooks/useOutsideClick';
import useTrapFocus from 'Hooks/useTrapFocus';

import { IQuickSearchTranslations } from 'Types/Litium';
import CtaButton from 'Components/CtaButton';
import Divider from 'Components/Divider';
import Loader from 'Components/Loader';

import CrossIcon from 'Icons/closeThin.svg?react';
import MagnicifactionGlassIcon from 'Icons/magnificationGlass.svg?react';

const { useGreater } = BreakPointHooks();
interface Props {
    searchTitle: string;
    searchResultPageUrl?: string;
    translations: IQuickSearchTranslations;
}

const QuickSearch = ({
    searchTitle,
    searchResultPageUrl,
    translations,
}: Props) => {
    const quickSearchInputRef = useRef<HTMLInputElement>(null);
    const quickSearchResultRef = useRef<HTMLUListElement>(null);
    const [, setLockedBody] = useLockedBody(false);
    const isGreater = useGreater('lg');

    const [showQuickSearch, setShowQuickSearch] = useState(false);
    const [quickSearchTerm, setQuickSearchTerm] = useState('');
    const trapRef = useTrapFocus(showQuickSearch, () =>
        setShowQuickSearch(false)
    );

    const clickOutsideQuickSearchRef = useOutsideClick(
        () => setShowQuickSearch(false),
        quickSearchInputRef
    );

    const handleQuickSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setQuickSearchTerm(e.currentTarget.value);
    };

    const [
        getQuickSearchResult,
        { data: quickSearchResults, isSuccess, isLoading },
    ] = useQuickSearchMutation();

    const debouncedSearchTerm = useDebounce(quickSearchTerm, 1000);

    const handleOnFocus = () => {
        if (quickSearchResults?.length > 1) {
            setShowQuickSearch(true);
        }
    };

    useEffect(() => {
        if (showQuickSearch && !isGreater) {
            setLockedBody(true);
        } else {
            setLockedBody(false);
        }
    }, [showQuickSearch, isGreater]);

    useEffect(() => {
        if (isSuccess && quickSearchResults?.length > 0) {
            setShowQuickSearch(true);
        }
    }, [quickSearchResults, isSuccess]);

    useEffect(() => {
        if (debouncedSearchTerm) {
            getQuickSearchResult(quickSearchTerm);
        }
    }, [debouncedSearchTerm]);

    const handleOnKeyDown = (e: React.KeyboardEvent) => {
        switch (e.key) {
            case 'Escape':
                setShowQuickSearch(false);
                break;
            case 'Tab': {
                setTimeout(() => {
                    const { activeElement } = document;
                    const isFocusInsideList =
                        quickSearchResultRef.current.contains(activeElement);
                    if (
                        !isFocusInsideList &&
                        document.activeElement !== quickSearchInputRef.current
                    ) {
                        setShowQuickSearch(false);
                    }
                }, 0);
                break;
            }
            default:
                return false;
        }
    };

    return (
        <form
            className="relative w-full px-4.5 lg:w-auto lg:px-0"
            action={searchResultPageUrl}
            method="GET"
        >
            <div
                style={
                    {
                        '--placeholder-width': `${searchTitle.length}ch`,
                    } as React.CSSProperties
                }
                className="relative flex h-10 w-full items-center gap-2.5 font-montserrat text-xs font-light lg:h-6 lg:w-[var(--placeholder-width)] lg:after:absolute lg:after:top-full lg:after:h-[1px] lg:after:w-full lg:after:bg-black xl:after:left-0"
            >
                <label
                    className="absolute left-2.5 lg:left-0"
                    htmlFor="q"
                    id="quicksearchLabel"
                >
                    <MagnicifactionGlassIcon className="h-6 w-6" />
                    <span className="sr-only">{searchTitle}</span>
                </label>
                <input
                    id="q"
                    type="text"
                    name="q"
                    aria-labelledby="quicksearchLabel"
                    className="h-full w-full rounded-sm bg-merino pl-[42px] font-montserrat text-xs font-light text-black placeholder:font-montserrat placeholder:font-light placeholder:text-black  lg:h-auto lg:bg-white lg:py-1.5 lg:pl-6"
                    placeholder={searchTitle}
                    ref={quickSearchInputRef}
                    onKeyDown={handleOnKeyDown}
                    onFocus={handleOnFocus}
                    onChange={handleQuickSearch}
                    required
                />
            </div>

            <div
                ref={
                    clickOutsideQuickSearchRef as React.MutableRefObject<HTMLDivElement>
                }
                className={`scroll-bar-gsd::-webkit-scrollbar scroll-bar-gsd::-webkit-scrollbar-thumb scroll-bar-gsd absolute left-0 top-full mt-3 max-h-96 w-full overflow-y-auto shadow-lg lg:mt-4.5 lg:w-72 lg:bg-white lg:p-0 lg:px-0 ${
                    showQuickSearch ? 'block' : 'hidden'
                }`}
            >
                <div ref={trapRef}>
                    <ul
                        ref={quickSearchResultRef}
                        className="flex flex-col gap-2 bg-white p-4.5"
                    >
                        {quickSearchResults
                            ? quickSearchResults.map((item, index) => (
                                  <Fragment key={`${item.url}-${index}`}>
                                      {!item.showAll &&
                                      item.category !== 'NoHit' ? (
                                          <>
                                              {quickSearchResults[index - 1]
                                                  ?.category !==
                                                  item.category && (
                                                  <li
                                                      className={`relative ${
                                                          index === 0
                                                              ? ''
                                                              : 'mt-2'
                                                      }`}
                                                  >
                                                      {quickSearchResults[0] !==
                                                          quickSearchResults[
                                                              index
                                                          ] && (
                                                          <Divider margin="my-4" />
                                                      )}
                                                      <div className="flex flex-nowrap items-center justify-between gap-4 gap-x-4">
                                                          <h5 className="my flex flex-wrap font-montserrat text-xl">
                                                              {item.category}
                                                          </h5>
                                                          {index === 0 && (
                                                              <button
                                                                  title={
                                                                      translations.close
                                                                  }
                                                                  type="button"
                                                                  onClick={() =>
                                                                      setShowQuickSearch(
                                                                          false
                                                                      )
                                                                  }
                                                              >
                                                                  <span className="sr-only">
                                                                      {
                                                                          translations.close
                                                                      }
                                                                  </span>
                                                                  <CrossIcon className="h-7 w-7" />
                                                              </button>
                                                          )}
                                                      </div>
                                                  </li>
                                              )}
                                              {item.url && (
                                                  <li>
                                                      <a
                                                          className="group/item flex flex-nowrap items-center gap-5 text-sm"
                                                          href={item.url}
                                                      >
                                                          {item.hasImage &&
                                                              item.imageSource && (
                                                                  <div
                                                                      className={`relative h-20 w-14 min-w-14 max-w-14 @container ${
                                                                          item.isProduct &&
                                                                          !item.hasAnyVariantEnabled
                                                                              ? 'before:absolute before:h-full before:w-full before:bg-white/70 before:backdrop-blur-[1px]'
                                                                              : ''
                                                                      }`}
                                                                  >
                                                                      <img
                                                                          alt={
                                                                              item.name
                                                                          }
                                                                          width="56"
                                                                          height="80"
                                                                          src={`${item.imageSource}?format=webp&width=56&height=80&fit=crop`}
                                                                      />
                                                                      {item.isProduct &&
                                                                          !item.hasAnyVariantEnabled && (
                                                                              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-[10deg] text-center text-xs">
                                                                                  {
                                                                                      translations.outOfStock
                                                                                  }
                                                                              </span>
                                                                          )}
                                                                  </div>
                                                              )}
                                                          <span className="self-start font-roboto font-bold group-hover/item:underline">
                                                              {item.name}
                                                          </span>
                                                      </a>
                                                  </li>
                                              )}
                                          </>
                                      ) : (
                                          <li>
                                              {item.category === 'NoHit' ? (
                                                  <p>{item.name}</p>
                                              ) : (
                                                  <CtaButton className="w-full">
                                                      {item.name}
                                                  </CtaButton>
                                              )}
                                          </li>
                                      )}
                                  </Fragment>
                              ))
                            : isLoading && (
                                  <li>
                                      <Loader size="10px" marginY="my-2" />
                                  </li>
                              )}
                    </ul>
                </div>
            </div>
        </form>
    );
};

export default QuickSearch;
